import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Trash2, Plus, Search } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';

const CartManager = ({ cartId, leadId, onClose }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('All');
  const [selectedPackage, setSelectedPackage] = useState('All');
  const [quantity, setQuantity] = useState(1);

  const { data: items = [], refetch: refetchItems } = useQuery({
    queryKey: ['cartItems', cartId],
    queryFn: async () => {
      if (!cartId) return [];
      return await pb.collection('cart_items').getFullList({
        filter: `cartId = "${cartId}"`,
      });
    },
    enabled: !!cartId,
  });

  const { data: priceItems = [] } = useQuery({
    queryKey: ['priceList'],
    queryFn: () => pb.collection('price_list_items').getFullList({ sort: 'buildingType,name' }),
  });

  const removeItem = useMutation({
    mutationFn: (itemId) => pb.collection('cart_items').delete(itemId),
    onSuccess: () => {
      toast.success('Item removed');
      refetchItems();
      queryClient.invalidateQueries(['leadCart', leadId]);
    },
    onError: (err) => toast.error('Failed to remove: ' + err.message),
  });

  const addItem = useMutation({
    mutationFn: async ({ priceItem, qty }) => {
      const existing = items.find(i => i.priceItemId === priceItem.id);
      if (existing) {
        const newQty = existing.quantity + qty;
        return pb.collection('cart_items').update(existing.id, { quantity: newQty });
      } else {
        return pb.collection('cart_items').create({
          cartId,
          priceItemId: priceItem.id,
          name: priceItem.name,
          description: priceItem.details || '',
          unit: priceItem.unit || 'pc',
          quantity: qty,
          rate: priceItem.sellingPrice,
          mrp: priceItem.mrp,
        });
      }
    },
    onSuccess: () => {
      toast.success('Item added');
      refetchItems();
      queryClient.invalidateQueries(['leadCart', leadId]);
    },
    onError: (err) => toast.error('Failed to add: ' + err.message),
  });

  const buildingOptions = useMemo(
    () => ['All', ...new Set(priceItems.map(p => p.buildingType).filter(Boolean))],
    [priceItems]
  );
  const packageOptions = useMemo(
    () => ['All', ...new Set(priceItems.map(p => p.packageType).filter(Boolean))],
    [priceItems]
  );
  const filteredPackages = useMemo(() => {
    return priceItems.filter(pkg => {
      const matchBuilding = selectedBuilding === 'All' || pkg.buildingType === selectedBuilding;
      const matchPackage = selectedPackage === 'All' || pkg.packageType === selectedPackage;
      const matchSearch = !searchTerm || pkg.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchBuilding && matchPackage && matchSearch;
    });
  }, [priceItems, selectedBuilding, selectedPackage, searchTerm]);

  const totalAmount = items.reduce((sum, i) => sum + i.rate * i.quantity, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[150px]">
          <Label>Search Packages</Label>
          <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="w-40">
          <Label>Building</Label>
          <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              {buildingOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label>Package Type</Label>
          <Select value={selectedPackage} onValueChange={setSelectedPackage}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              {packageOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-24">
          <Label>Qty</Label>
          <Input type="number" min="1" value={quantity} onChange={e => setQuantity(Number(e.target.value))} />
        </div>
      </div>

      <div className="border rounded-lg p-2 max-h-60 overflow-y-auto">
        {filteredPackages.length === 0 ? (
          <p className="text-center text-slate-500 py-4">No packages found</p>
        ) : (
          filteredPackages.map(pkg => (
            <div key={pkg.id} className="flex justify-between items-center py-2 border-b last:border-0">
              <div>
                <p className="font-medium">{pkg.name}</p>
                <p className="text-xs text-slate-500">{pkg.buildingType} • {pkg.packageType}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold text-amber-600">₹{pkg.sellingPrice}</span>
                <Button size="sm" onClick={() => addItem.mutate({ priceItem: pkg, qty: quantity })}>
                  <Plus className="w-4 h-4" /> Add
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <div>
        <h4 className="font-semibold mb-2">Current Cart Items</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Cart is empty</TableCell></TableRow>
            ) : (
              items.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>₹{item.rate}</TableCell>
                  <TableCell>₹{item.rate * item.quantity}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => removeItem.mutate(item.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {items.length > 0 && (
          <div className="text-right font-bold mt-2">Total: ₹{totalAmount}</div>
        )}
      </div>
    </div>
  );
};

export default CartManager;